import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ApiRefusal } from '@gatecheckpro/api-client';
import { Card, Notice } from '@gatecheckpro/design-system';
import { api } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { FailureNotice } from '../lib/refusal.tsx';

/**
 * Where CABS Identity returns the browser for the organizer console.
 *
 * This origin's own callback, posting to this origin's own API. The ceremony recorded which surface
 * began it, and the endpoint this page posts to states which surface is completing it; the server
 * refuses a mismatch. So a callback URL from another console, replayed here, cannot establish a
 * session on this origin — even though the OAuth exchange behind it would be perfectly valid.
 *
 * Like the attendee callback, this page holds no PKCE material and parses nothing that matters. It
 * hands the whole URL to the server, which owns every check, and stores only the session it gets
 * back — through the same client the password sign-in uses, so there is one session representation
 * rather than two.
 */
export function CabsCallbackPage() {
  const navigate = useNavigate();
  const { reload } = useSession();
  const [error, setError] = useState<unknown>(null);
  /** A person who has never been linked here is not a failure; they are simply not staff yet. */
  const [unbound, setUnbound] = useState(false);
  // The ceremony is single-use, and React runs effects twice in development StrictMode.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    void (async () => {
      try {
        const session = await api.request<{
          accessToken: string;
          accessTokenExpiresAt: string;
          sessionToken: string;
          sessionExpiresAt: string;
          mfaSatisfied: boolean;
          returnTo: string;
        }>('/api/v1/auth/cabs/organizer/callback', {
          method: 'POST',
          anonymous: true,
          body: { currentUrl: window.location.href },
        });

        api.setSession(session);
        await reload();

        /*
         * Same-origin only, and deliberately so. The server authorised `returnTo` before the
         * browser left, and this console has no business sending anyone to another surface: a
         * session established here cannot be read there anyway.
         */
        const target = new URL(session.returnTo);
        void navigate(
          target.origin === window.location.origin
            ? `${target.pathname}${target.search}${target.hash}`
            : '/organizations',
          { replace: true },
        );
      } catch (failure) {
        // Not logged: this URL carries an authorization code and the refusal carries the server's
        // reason, and neither belongs in a console extensions can read.
        if (failure instanceof ApiRefusal && failure.code === 'AUTH_CABS_SUBJECT_NOT_BOUND') {
          setUnbound(true);
          return;
        }
        setError(failure);
      }
    })();
  }, [navigate, reload]);

  /*
   * Refused, and nothing offered.
   *
   * On the attendee surface an unbound identity is invited to create an account. Here it must not
   * be: membership and capability in GateCheckPro come from its own authorization, and "signed in
   * at CABS" must never become a way to appear in a staff console.
   */
  if (unbound) {
    return (
      <Card>
        <h1>This identity has no organizer access</h1>
        <Notice tone="caution" title="Not linked to a GateCheckPro account">
          Your CABS identity is not linked to an account with access here. Ask an administrator to
          invite you, then link your identity from a signed-in session.
        </Notice>
        <p>
          <a href="/sign-in">Back to sign in</a>
        </p>
      </Card>
    );
  }

  if (error !== null) {
    return (
      <Card>
        <h1>Sign-in could not be completed</h1>
        <FailureNotice error={error} />
        <p>
          <a href="/sign-in">Return to sign in</a>
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h1>Completing sign-in</h1>
      <Notice tone="info">Finishing your sign-in with CABS Identity.</Notice>
    </Card>
  );
}
